import { cineamoApiClient } from '../../apiClient';
import { Client } from '../../constants/Client.enum';
import { RequestConfig } from '../../fetch-helper';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { CartDto } from '../../models/cart/CartDto.types';
import { PriceOptionDto } from '../../models/price-option/PriceOptionDto.types';
import { PriceOptionInputDto } from '../../models/price-option/PriceOptionInputDto.types';
import { ShowingDto } from '../../models/showing/ShowingDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { PriceOptionListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ Single PriceOption ------------------------>

/**
 * This endpoint returns a single priceOption.
 *
 * @example
 * // Get the details of a single priceOption by priceOptionId or slug:
 * getPriceOptionById(1);
 *
 * @param id
 * @returns {ApiResponse<PriceOptionDto>}
 */

export async function getPriceOptionById(id: number | string): Promise<ApiResponse<PriceOptionDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<PriceOptionDto>>('/price-options/' + id)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ PriceOptions ------------------------>

export type GetPriceOptionsQuery = {
    cinemaId?: number;
    showingIds?: number[];
    /**
     * The Cinetixx terminal slot whose prices to list. With `showingIds` and no `client`, the default terminal's
     * prices are returned.
     */
    client?: Client;
} & UrlQueryParameter;

/**
 * This endpoint returns a filtered list of price-options.
 *
 * @example
 * // Get a list price-options filtered according to query parameters used:
 * getPriceOptions({isActive: true});
 *
 * @returns {ApiResponse<PriceOptionListResponse>}
 */

export async function getPriceOptions(
    params?: GetPriceOptionsQuery,
    config?: RequestConfig
): Promise<ApiResponse<PriceOptionListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<PriceOptionListResponse>>(`/price-options${buildUrlQuery(params)}`, config)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Create PriceOption ------------------------>

/**
 * This endpoint creates a single priceOption and returns a priceOption object or an error.
 *
 * @example
 * // Create a single priceOption from form data:
 * createPriceOption({...priceOption});
 *
 * @param priceOption
 * @returns {ApiResponse<PriceOptionDto> | ApiErrorResponse<PriceOptionDto>}
 */

export async function createPriceOption(
    priceOption: PriceOptionInputDto
): Promise<ApiResponse<PriceOptionDto> | ApiErrorResponse<PriceOptionInputDto>> {
    return cineamoApiClient
        .post('/price-options', priceOption)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Update PriceOption ------------------------>

/**
 * This endpoint updates a single priceOption and returns the updated priceOption object or an error.
 *
 * @example
 * // Update the details of a single priceOption from form data:
 * updatePriceOptionById({priceNet: 456});
 *
 * @param priceOption
 * @param priceOptionId
 * @returns {ApiResponse<PriceOptionDto>}
 */

export async function updatePriceOptionById(
    priceOptionId: number,
    priceOption: PriceOptionInputDto
): Promise<ApiResponse<PriceOptionDto> | ApiErrorResponse<PriceOptionInputDto>> {
    return cineamoApiClient
        .patch(`price-options/${priceOptionId}`, priceOption)
        .then((res) => res.data)
        .catch((error) => error);
}

// <------------------------ Delete Price Option ------------------------>

/**
 * This endpoint returns a boolean.
 *
 * @example
 * // Delete a single price option by id:
 * deletePriceOptionById(1);
 *
 * @param priceOption
 * @returns {boolean}
 */

export async function deletePriceOptionById(priceOption: number | string): Promise<boolean> {
    return cineamoApiClient
        .delete(`/price-options/${priceOption}`)
        .then((response) => response.status === 204)
        .catch(() => {
            return false;
        });
}

// <------------------------ PriceOptions For the Cart ------------------------>

/**
 * This endpoint returns a filtered list of price-options.
 *
 * @example
 * // Get a list price-options filtered according to query parameters used:
 * getPriceOptionsForCart('123-456-789', {isActive: true});
 *
 * @returns {ApiResponse<PriceOptionListResponse>}
 */

export async function getPriceOptionsForCart(
    cartUuid: CartDto['uuid'],
    params: GetPriceOptionsQuery & {
        showingId: ShowingDto['id'];
    },
    config?: RequestConfig
): Promise<ApiResponse<PriceOptionListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<PriceOptionListResponse>>(`/carts/${cartUuid}/price-options${buildUrlQuery(params)}`, {
            ...config,
            session: {
                scheme: 'CineamoCart',
                accessToken: cartUuid
            }
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}
